import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DatasetClientService } from '@mode/dataset/data-access';
import { DataViewFacade, SwitchViewFacade, SwitchViewActions } from '@mode/switch-views/contract';
import { ToastService } from '@mode/shared/util-toast';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  combineLatestWith,
  concatMap,
  map,
  of,
  switchMap,
  tap,
  filter,
  withLatestFrom,
  mergeMap,
  take,
} from 'rxjs';
import {
  DatasetActions,
  PublishDatasetEvents,
  PublishDialogService,
  RepublishDialogService,
  UnpublishDialogService,
  DatasetPublishingFlagStates,
  PublishDatasetSocketSuccessPayload,
} from '@mode/dataset/contract';
import { DatasetService } from './dataset.service';
import {
  ConfirmDialog,
  FeatureFlag,
  FeatureFlagsFacade,
  ProducerTypes,
  PublicationState,
  ThoughtspotPublication,
  SwitchViewDomain,
} from '@mode/shared/contract-common';
import {
  DatasetPublishErrorComponent,
  DatasetPublishProgressComponent,
  DatasetPublishSuccessComponent,
  DatasetPublishTsSuccessComponent,
} from '@mode/dataset/util';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ReportFacade } from '@mode/reports/domain';
import { CellExportFacade } from '@mode/notebooks/contract';
import { QueryResource } from 'libs/shared/data-access-webapp/src/lib/queries/query-response.types';
import { CellExportActions } from '@mode/notebooks/contract';
import { MoveSpacePublishService } from '../move-space-publish/move-space-publish.service';
import { SocketSubscription } from '@mode/shared/data-access-socket';

@Injectable()
export class DatasetEffects {
  deleteDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.deleteDataset),
      concatMap((action) =>
        this.datasetService.deleteDataset(action.datasetToken).pipe(
          map(() => DatasetActions.deleteDatasetSuccess()),
          catchError(() => of(DatasetActions.deleteDatasetFail()))
        )
      )
    )
  );

  deleteDatasetSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.deleteDatasetSuccess),
        tap(() => this.toastService.success('Deleted Dataset'))
      ),
    { dispatch: false }
  );

  deleteDatasetError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.deleteDatasetFail),
        tap(() => this.toastService.error('Failed to delete Dataset'))
      ),
    { dispatch: false }
  );

  loadDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DatasetActions.loadDatasetViewRoute,
        DatasetActions.loadDatasetEditRoute,
        DatasetActions.runDatasetPollingCompleted
      ),
      switchMap(({ datasetToken }) =>
        this.getDataset(datasetToken).pipe(
          map((dataset) =>
            DatasetActions.loadDatasetSuccess({
              token: datasetToken,
              datasetResult: dataset.datasetResult,
              switchView: dataset.switchView,
              details: dataset.details,
            })
          ),
          catchError((error: HttpErrorResponse) => of(DatasetActions.loadDatasetFailure({ error })))
        )
      )
    )
  );

  loadImportedDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.loadDatasetReportEditRoute),
      switchMap(({ datasetToken }) =>
        this.getDataset(datasetToken).pipe(
          map((dataset) =>
            DatasetActions.loadDatasetReportEditRouteSuccess({
              dataset: {
                token: datasetToken,
                result: dataset.datasetResult,
                switchView: dataset.switchView,
                details: dataset.details,
              },
            })
          )
        )
      )
    )
  );

  loadNotebookCellDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.loadNotebookCellDatasetEditRoute),
      switchMap(({ datasetToken, switchViewToken }) =>
        this.getNotebookCellDataset(datasetToken, switchViewToken).pipe(
          map((dataset) =>
            DatasetActions.loadDatasetReportEditRouteSuccess({
              dataset: {
                token: datasetToken,
                result: {
                  ...dataset.datasetResult,
                  ...(dataset.thoughtspot ? { thoughtspot: dataset.thoughtspot } : {}),
                  ...(dataset.tsPublishMetaData ? { tsPublishMetaData: dataset.tsPublishMetaData } : {}),
                },
                switchView: dataset.switchView,
                details: dataset.details,
              },
            })
          )
        )
      )
    )
  );

  openDeleteDatasetModalFromEditor$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.openDeleteDatasetModal),
        withLatestFrom(this.datasetFacade.isDatasetPublished$),
        map(([action, isPublished]) => this.openDialog(true, action.token, isPublished))
      ),
    { dispatch: false }
  );

  openDeleteDatasetModalFromCollection$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          DatasetActions.openDeleteDatasetModalFromCollectionList,
          DatasetActions.openDeleteDatasetModalFromCollectionTile,
          DatasetActions.openDeleteDatasetModalFromCollectionSidePanel
        ),
        switchMap((action) =>
          this.getDataset(action.token).pipe(
            map((dataset) => ({
              action,
              publishedAt: dataset.details?.thoughtspot?.publishedAt,
            })),
            catchError((error) => {
              console.error('Error fetching dataset during delete:', error);
              return of({
                action,
                publishedAt: false,
              });
            })
          )
        ),
        map(({ action, publishedAt }) => {
          this.openDialog(false, action.token, !!publishedAt);
        })
      ),
    { dispatch: false }
  );

  deleteDatasetFromCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.deleteFromCollection),
      concatMap((action) =>
        this.datasetService.deleteDataset(action.token).pipe(
          map(
            () => DatasetActions.deleteFromCollectionView({ token: action.token }),
            catchError(() => of(DatasetActions.deleteDatasetFail()))
          )
        )
      )
    )
  );

  openPublishModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.openPublishModal),
        withLatestFrom(
          this.featureFlagsFacade.asObservable(FeatureFlag.NotebookDatasetPublishUpdates),
          this.dataViewFacade.enhancedDatasetResults$,
          this.reportFacade.isPersonalCollection$
        ),
        tap(([action, publishUpdatesEnabled, datasets, isPersonal]) => {
          const isAnyDatasetPublished = datasets.some((dataset) => dataset?.thoughtspot?.publishedAt);
          // check if updates Enabled and any dataset is published and collection is in personal
          if (publishUpdatesEnabled && !isAnyDatasetPublished && isPersonal) {
            return this.moveSpacePublishService.triggerPublishMoveSpace(action.switchViewToken);
          } else {
            return this.publishDatasetDialogService.open(action.switchViewToken);
          }
        })
      ),
    { dispatch: false }
  );

  publishDatasetAfterMoveSpace$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.publishDatasetAfterMoveSpace),
        tap((action) => this.publishDatasetDialogService.open(action.switchViewToken))
      ),
    { dispatch: false }
  );

  openRepublishModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.openRepublishModal),
        tap((action) =>
          this.republishDatasetDialogService.open(action.switchViewToken, action.queryToken, action.selectedText)
        )
      ),
    { dispatch: false }
  );

  openUnpublishModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.openUnpublishModal),
        tap((action) => this.unpublishDatasetDialogService.open(action.switchViewToken))
      ),
    { dispatch: false }
  );

  publishDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.publishDataset),
      withLatestFrom(this.featureFlagsFacade.asObservable(FeatureFlag.NotebookDatasetPublishUpdates)),
      switchMap(([{ name, datasetToken, producerType, queryToken, switchViewToken }, publishUpdatesEnabled]) =>
        this.datasetService.publishDataset(datasetToken, name, producerType, queryToken).pipe(
          map((datasetPublicationResponse) => {
            const payload = {
              name,
              datasetToken,
              producerType,
              queryToken,
              switchViewToken,
              datasetPublicationResponse,
            };
            return publishUpdatesEnabled
              ? DatasetActions.initPublishDatasetSuccess(payload)
              : DatasetActions.publishDatasetSuccess(payload);
          }),
          catchError((error) => {
            console.error('Error', error);
            return of(DatasetActions.publishDatasetFailure({ error }));
          })
        )
      )
    )
  );

  /**
   * This is called when publishUpdates flag is not enabled start
   **/

  publishDatasetSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.publishDatasetSuccess),
        combineLatestWith(
          this.featureFlagsFacade.featureValueAsObservable<DatasetPublishingFlagStates>(FeatureFlag.DatasetPublishing)
        ),
        tap(([{ producerType, name, datasetPublicationResponse }]) => {
          // Header should update only for resuable dataset during publish
          if (producerType !== ProducerTypes.NotebookCell) {
            this.reportFacade.updateReportAfterDatasetPublish(name, datasetPublicationResponse.publishedAt);
          }
        }),
        tap(([{ datasetPublicationResponse }, publishReusableDatasetFlag]) => {
          if (publishReusableDatasetFlag === DatasetPublishingFlagStates.ENABLED_ALPHA) {
            this.toastService.success('Publication initiated');
          } else {
            const thoughtspotClusterDomain = new URL(datasetPublicationResponse.publishUrl).origin;
            const thoughtspotDataWorkspaceLink = `${thoughtspotClusterDomain}/#/data/tables`;
            this.toastService.success('', {
              disableTimeout: true,
              tapToDismiss: false,
              toastComponent: DatasetPublishSuccessComponent,
              payload: { thoughtspotDataWorkspaceLink },
            });
          }
        }),
        filter(
          ([{ producerType }]) => producerType === ProducerTypes.NotebookCell // Refresh Runs Only for Exported Dataset
        ),
        map(([{ datasetToken, queryToken }]) =>
          CellExportActions.refreshDataViewsAfterExport({
            reportToken: datasetToken,
            exportedDatasetToken: queryToken ?? '',
          })
        )
      ),
    { dispatch: true }
  );

  publishDatasetFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.publishDatasetFailure),
        tap(() => {
          this.toastService.error('Dataset failed to publish. Please try again');
        })
      ),
    { dispatch: false }
  );

  /**
   * This is called when publishUpdates flag is not enabled end
   **/

  /**
   * This is called when publishUpdates flag is enabled start
   **/

  // This is for local datasets
  triggerPublishToast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SwitchViewActions.loadEditorViewsSuccess),
      withLatestFrom(this.featureFlagsFacade.asObservable(FeatureFlag.NotebookDatasetPublishUpdates)),
      filter(([_, publishUpdatesEnabled]) => publishUpdatesEnabled),
      mergeMap(([{ dataViews }]) => {
        const actions = dataViews
          .filter(
            (dataView: SwitchViewDomain.DataView) =>
              dataView.thoughtspot?.publicationState === PublicationState.Submitted
          )
          .map((dataView: SwitchViewDomain.DataView) =>
            DatasetActions.initPublishDatasetSuccess({
              name: dataView.datasetResult.queryName,
              datasetToken: dataView.datasetResult.datasetToken,
              producerType: dataView.datasetResult.producerType,
              queryToken: dataView.datasetResult.queryToken,
              switchViewToken: dataView.datasetResult.switchViewToken,
              datasetPublicationResponse: dataView.thoughtspot as ThoughtspotPublication,
            })
          );
        return actions;
      })
    )
  );

  // This is for imported datasets
  initiatePublishToast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.loadDatasetEditRoute),
      withLatestFrom(this.featureFlagsFacade.asObservable(FeatureFlag.NotebookDatasetPublishUpdates)),
      filter(([, publishUpdatesEnabled]) => publishUpdatesEnabled),
      switchMap(([action]) =>
        this.getDataset(action.datasetToken).pipe(
          filter((dataset) => dataset.details?.thoughtspot?.publicationState === PublicationState.Submitted),
          map((dataset) =>
            DatasetActions.initPublishDatasetSuccess({
              name: dataset.details?.name ?? '',
              datasetToken: dataset.datasetResult.datasetToken,
              producerType: dataset.datasetResult.producerType,
              queryToken: dataset.datasetResult.queryToken,
              switchViewToken: dataset.datasetResult.switchViewToken,
              datasetPublicationResponse: dataset.details?.thoughtspot as ThoughtspotPublication,
            })
          )
        )
      )
    )
  );

  initPublishDatasetSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.initPublishDatasetSuccess),
      tap(({ producerType, name, datasetPublicationResponse }) => {
        if (producerType !== ProducerTypes.NotebookCell) {
          this.reportFacade.updateReportAfterDatasetPublish(name, datasetPublicationResponse.publishedAt);
        }
      }),
      map(({ datasetToken, producerType, queryToken, switchViewToken, datasetPublicationResponse }) => {
        let publishDatasetSubscription = {} as SocketSubscription<PublishDatasetSocketSuccessPayload>;
        try {
          publishDatasetSubscription = this.datasetService.connectToPublishDatasetSocket(
            datasetPublicationResponse.datasetThoughtspotPublicationToken ?? ''
          );
        } catch (error: any) {
          error.datasetToken = datasetToken;
          throw error;
        }
        return DatasetActions.publishDatasetSocketConnectionSuccess({
          datasetToken,
          producerType,
          queryToken,
          switchViewToken,
          publishDatasetSubscription,
          datasetPublicationResponse,
        });
      }),
      catchError(({ switchViewToken, datasetToken }) =>
        of(DatasetActions.publishDatasetTsFailed({ switchViewToken, datasetToken }))
      )
    )
  );

  initPublishDatasetFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.initPublishDatasetFailure),
      map(({ switchViewToken, datasetToken }) =>
        DatasetActions.publishDatasetTsFailed({ switchViewToken, datasetToken })
      )
    )
  );

  publishDatasetSocketConnectionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.publishDatasetSocketConnectionSuccess),
      mergeMap(
        ({
          datasetToken,
          producerType,
          switchViewToken,
          queryToken,
          publishDatasetSubscription,
          datasetPublicationResponse,
        }) =>
          publishDatasetSubscription.eventBus.pipe(
            map((event) => {
              switch (event.name) {
                case PublishDatasetEvents.PublishDatasetSuccess: {
                  publishDatasetSubscription.disconnect();
                  return DatasetActions.publishDatasetTsSuccess({
                    datasetToken,
                    producerType,
                    queryToken,
                    switchViewToken,
                    publishedUrl: event?.data?.thoughtspot_publish_url ?? 'http://example.com',
                    datasetPublicationResponse: {
                      ...datasetPublicationResponse,
                      publicationState: event?.data?.state as PublicationState,
                    },
                  });
                }
                case PublishDatasetEvents.PublishDatasetFailed: {
                  publishDatasetSubscription.disconnect();
                  return DatasetActions.publishDatasetTsFailed({ switchViewToken, datasetToken });
                }
              }
              return DatasetActions.publishDatasetTsPending({ switchViewToken });
            })
          )
      )
    )
  );

  publishDatasetTsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.publishDatasetTsSuccess),
        mergeMap(({ publishedUrl, switchViewToken, producerType, datasetToken, queryToken }) =>
          this.switchViewFacade.getEnhancedDataseResult(switchViewToken ?? '').pipe(
            take(1),
            tap((dataset) => {
              const toastId = dataset.progressToastId;
              if (toastId) {
                this.toastService.remove(toastId);
              }

              const thoughtspotClusterDomain = new URL(publishedUrl)?.origin;
              const thoughtspotDataWorkspaceLink = `${thoughtspotClusterDomain}/#/data/tables`;

              this.toastService.success('', {
                closeButton: true,
                disableTimeout: true,
                tapToDismiss: false,
                toastComponent: DatasetPublishTsSuccessComponent,
                payload: { publishedUrl: thoughtspotDataWorkspaceLink },
              });
            }),
            filter(() => producerType === ProducerTypes.NotebookCell),
            map(() =>
              CellExportActions.refreshDataViewsAfterExport({
                reportToken: datasetToken,
                exportedDatasetToken: queryToken ?? '',
              })
            )
          )
        )
      ),
    { dispatch: true }
  );

  publishDatasetTsFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.publishDatasetTsFailed),
        switchMap(({ switchViewToken }) =>
          this.switchViewFacade
            .getEnhancedDataseResult(switchViewToken ?? '')
            .pipe(map((dataset) => dataset.progressToastId))
        ),
        tap((toastId) => {
          if (toastId) {
            this.toastService.remove(toastId);
          }
          this.toastService.error('', {
            closeButton: true,
            disableTimeout: false,
            tapToDismiss: false,
            toastComponent: DatasetPublishErrorComponent,
          });
        })
      ),
    { dispatch: false }
  );

  publishDatasetTsPending$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.initPublishDatasetSuccess),
      withLatestFrom(this.featureFlagsFacade.asObservable(FeatureFlag.NotebookDatasetPublishUpdates)),
      filter(([, publishDatasetUpdatesFlag]) => publishDatasetUpdatesFlag),
      map(([{ switchViewToken }]) => ({
        switchViewToken,
        activeToast: this.toastService.info<unknown, DatasetPublishProgressComponent>('', {
          disableTimeout: true,
          tapToDismiss: false,
          toastComponent: DatasetPublishProgressComponent,
        }),
      })),
      map(({ switchViewToken, activeToast }) =>
        DatasetActions.publishDatasetTsInProgress({ switchViewToken, progressToastId: activeToast.toastId })
      )
    )
  );
  /**
   * This is called when publishUpdates flag is enabled end
   **/

  republishDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.republishDataset),
      switchMap((action) =>
        this.datasetService.republishDataset(action.datasetThoughtspotPublicationToken).pipe(
          map(() => DatasetActions.republishDatasetSuccess()),
          catchError((error) => {
            console.error('Error', error);
            return of(DatasetActions.republishDatasetFailure({ error }));
          })
        )
      )
    )
  );

  republishDatasetSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.republishDatasetSuccess),
        tap(() => {
          this.toastService.success('Dataset republished');
        })
      ),
    { dispatch: false }
  );

  republishDatasetFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.republishDatasetFailure),
        tap(() => {
          this.toastService.error('Dataset failed to republish. Please try again');
        })
      ),
    { dispatch: false }
  );

  unpublishDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.unpublishDataset),
      switchMap(({ datasetToken, switchViewToken, producerType, datasetThoughtspotPublicationToken }) =>
        this.datasetService.unpublishDataset(datasetToken, producerType, datasetThoughtspotPublicationToken).pipe(
          map(() => DatasetActions.unpublishDatasetSuccess({ switchViewToken })),
          catchError((error) => {
            console.error('Error', error);
            return of(DatasetActions.unpublishDatasetFailure({ error }));
          })
        )
      )
    )
  );

  unpublishDatasetSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.unpublishDatasetSuccess),
        tap(() => {
          this.toastService.success('Dataset unpublished');
        })
      ),
    { dispatch: false }
  );

  unpublishDatasetFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.unpublishDatasetFailure),
        tap(() => {
          this.toastService.error('Dataset failed to unpublish. Please try again');
        })
      ),
    { dispatch: false }
  );

  fetchExportedDatasetSource$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.fetchExportedDatasetSource),
      withLatestFrom(this.cellExportFacade.queries$),
      concatMap(([action, queries]) => {
        const matchedDataset = queries?.getRecords()?.find((query: QueryResource) => query.token === action.queryToken);
        if (!matchedDataset) return of(DatasetActions.fetchExportedDatasetSourceFailure({}));
        return this.datasetService.fetchSourceForExportedDataset(matchedDataset).pipe(
          map((response) => DatasetActions.fetchExportedDatasetSourceSuccess({ response })),
          catchError((error) => of(DatasetActions.fetchExportedDatasetSourceFailure({ error })))
        );
      })
    )
  );

  private getDataset(datasetToken: string) {
    return this.switchViewFacade.getDataset(datasetToken);
  }

  private getNotebookCellDataset(datasetToken: string, switchViewToken: string) {
    return this.switchViewFacade.getNotebookCellDataset(datasetToken, switchViewToken);
  }

  public openDialog(isFromEditor: boolean, token: string, isPublished = false) {
    this.confirmDialog.open({
      dialogTitle: `Delete ${isPublished ? 'published' : ''} Dataset?`,
      dialogText: `
          <p>This action is irreversible and will remove the Dataset ${
            isPublished
              ? 'and permanently break dependent content in Analyst Studio and the data workspace.'
              : 'from your Workspace.'
          }</p>
          ${
            isPublished
              ? `
            <p class='bold-text unset-margin'>The following will be deleted:</p>
            <ul>
              <li>any Calculated Fields that were added to the Dataset</li>
              <li>any schedules that were added to the Dataset</li>
            </ul>
            <p class='bold-text unset-margin'>The following will break:</p>
            <ul class='unset-margin'>
              <li>the published table imported in the Data workspace</li>
              <li>any dependent Models or Worksheets</li>
              <li>any dependent Reports or Liveboards</li>
              <li>any dependent Answers</li>
              <li>any dependent calculated fields</li>
            </ul>
            `
              : `
            <p class='bold-text unset-margin'>The following will be deleted:</p>
            <ul>
              <li>any dependent Explorations</li>
              <li>any Calculated Fields that were added to the Dataset</li>
            </ul>
            <p class='bold-text unset-margin'>The following will break:</p>
            <ul class='unset-margin'>
              <li>any dependent Reports</li>
              <li>any dependent Calculated Fields</li>
            </ul>
            `
          }
          `,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      acknowledgeText: isPublished ? `I understand that deleting is permanent and can't be undone` : '',
      confirmAction: () => {
        if (isFromEditor) {
          this.datasetFacade.deleteDataset(token);
        } else {
          this.datasetFacade.deleteFromCollection(token);
        }
      },
      confirmButtonColor: 'alert',
      confirmButtonType: 'primary',
    });
  }

  constructor(
    private actions$: Actions,
    private datasetService: DatasetClientService,
    private switchViewFacade: SwitchViewFacade,
    private toastService: ToastService,
    private datasetFacade: DatasetService,
    private reportFacade: ReportFacade,
    private confirmDialog: ConfirmDialog,
    private publishDatasetDialogService: PublishDialogService,
    private unpublishDatasetDialogService: UnpublishDialogService,
    private republishDatasetDialogService: RepublishDialogService,
    private featureFlagsFacade: FeatureFlagsFacade,
    private cellExportFacade: CellExportFacade,
    private dataViewFacade: DataViewFacade,
    private moveSpacePublishService: MoveSpacePublishService
  ) {}
}
